import { fn, get, hash } from '@ember/helper';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import {
  PrismButton,
  PrismCard,
  PrismComboSelectInput,
  PrismInput,
  PrismMessage,
  PrismTable,
  PrismTabsContainer,
  PrismTooltip,
} from '@mylighthouse/prism-ember';
import type {
  CustomEmberTableColumn,
  CustomEmberTableRow,
} from '@mylighthouse/prism-ember/types/components/prism/table';
import { task } from 'ember-concurrency';
import { t, tKey } from 'ember-intl';
import { gt } from 'ember-truth-helpers';
import GeneralDropdownsDropdownPageSize from 'lumen/components/general/dropdowns/dropdown-page-size';
import GeneralDropdownsTriggersTriggerPowerSelectChevron from 'lumen/components/general/dropdowns/triggers/trigger-power-select-chevron';
import GeneralTablesPaginationFooter from 'lumen/components/general/tables/pagination-footer';
import HotelSettingsCentralMappingTablesCellsTdBarBased from 'lumen/components/hotel/settings/central-mapping/tables/cells/td-bar-based';
import HotelSettingsCentralMappingTablesCellsTdCommissionable from 'lumen/components/hotel/settings/central-mapping/tables/cells/td-commissionable';
import HotelSettingsCentralMappingTablesCellsTdCostProfile from 'lumen/components/hotel/settings/central-mapping/tables/cells/td-cost-profile';
import HotelSettingsCentralMappingTablesCellsTdDescription from 'lumen/components/hotel/settings/central-mapping/tables/cells/td-description';
import HotelSettingsCentralMappingTablesCellsTdMapping from 'lumen/components/hotel/settings/central-mapping/tables/cells/td-mapping';
import HotelSettingsCentralMappingTablesCellsTdNumber from 'lumen/components/hotel/settings/central-mapping/tables/cells/td-number';
import HotelSettingsCentralMappingTablesCellsThAutomationBadge from 'lumen/components/hotel/settings/central-mapping/tables/cells/th-automation-badge';
import HotelSettingsCentralMappingTablesCellsThInventory from 'lumen/components/hotel/settings/central-mapping/tables/cells/th-inventory';
import HotelSettingsCentralMappingTablesCellsThMaxAdults from 'lumen/components/hotel/settings/central-mapping/tables/cells/th-max-adults';
import HotelSettingsCentralMappingTablesTableMappingBulkEditRow from 'lumen/components/hotel/settings/central-mapping/tables/table-mapping/bulk-edit-row';
import SettingsCentralMappingDropdownsDropdownFilter from 'lumen/components/settings/central-mapping/dropdowns/dropdown-filter';
import type { AppliedMappingFilterIds } from 'lumen/components/settings/central-mapping/dropdowns/dropdown-mapping-filter';
import SettingsCentralMappingDropdownsDropdownMappingFilter from 'lumen/components/settings/central-mapping/dropdowns/dropdown-mapping-filter';
import SettingsCentralMappingTablesCellsTdValue from 'lumen/components/settings/central-mapping/tables/cells/td-value';
import type { CentralMappingCategory, CostSettingProfile } from 'lumen/models';
import type {
  BarBased,
  Commissionable,
} from 'lumen/models/central-mapping/mapping-entry-market-segment';
import { isMappingEntryRoomType } from 'lumen/models/central-mapping/mapping-entry-room-type';
import {
  type Breakdown,
  BREAKDOWNS,
  type CentralMappingMappingEntryExtendedModel,
  FILTER_OPTIONS,
  type FilterOption,
  isPmsValueType,
  mappingEntryHasBarBased,
  mappingEntryHasCommissionable,
  mappingEntryHasCostProfile,
  MAX_ADULTS_IN_ROOM,
  MAX_ROOM_INVENTORY,
  PMS_VALUE_TYPE_TRANSLATIONS,
  PMS_VALUE_TYPES,
  type PmsValueType,
} from 'lumen/utils/central-mapping-constants';
import { getUserCostProfileToStore } from 'lumen/utils/central-mapping-cost-profile';
import { timeout } from 'lumen/utils/waiters';

import styles from './table-mapping.module.css';

export const COLUMN_PROPERTIES = {
  VALUE: 'value',
  DESCRIPTION: 'descriptionToDisplay',
  MAPPING: 'mapping',
  INVENTORY: 'inventory',
  MAX_ADULTS: 'maxAdults',
  BAR_BASED: 'barBased',
  COMMISSIONABLE: 'commissionable',
  COST_PROFILE: 'costProfile',
} as const;

export type ColumnProperties =
  (typeof COLUMN_PROPERTIES)[keyof typeof COLUMN_PROPERTIES];

interface TableMappingRow extends CustomEmberTableRow {
  [COLUMN_PROPERTIES.VALUE]: string;
  [COLUMN_PROPERTIES.DESCRIPTION]: string;
  [COLUMN_PROPERTIES.MAPPING]: string;
  [COLUMN_PROPERTIES.INVENTORY]?: number | null;
  [COLUMN_PROPERTIES.MAX_ADULTS]?: number | null;
  [COLUMN_PROPERTIES.COMMISSIONABLE]?: Commissionable;
  [COLUMN_PROPERTIES.COST_PROFILE]?: CostSettingProfile | null;
  mappingEntry: CentralMappingMappingEntryExtendedModel;
  setCategory: (category: CentralMappingCategory | null) => void;
  setCostProfile?: (costProfile: CostSettingProfile | null) => void;
}

export const SEARCH_INPUT_ICONS = {
  SEARCH: 'search',
  CROSS: 'cross',
} as const;

export type SearchInputIconType =
  (typeof SEARCH_INPUT_ICONS)[keyof typeof SEARCH_INPUT_ICONS];

interface HotelSettingsCentralMappingTablesTableMappingSignature {
  Args: {
    appliedFilters?: AppliedMappingFilterIds;
    applyFilters: (appliedFilters: AppliedMappingFilterIds) => void;
    breakdown: Breakdown;
    costProfiles?: CostSettingProfile[];
    curentPageSizeValueOption: {
      label: string;
      value: number;
    };
    currentPage: number;
    endIndexPage: number;
    filterOption: FilterOption;
    hasEditRights?: boolean;
    hasUnsavedChanges?: boolean;
    hierarchyCategories: CentralMappingCategory[];
    isCostManagedByMarket?: boolean;
    isLoading: boolean;
    isMappingReadOnly?: boolean;
    isSaving: boolean;
    lastPage: number;
    mapping: CentralMappingMappingEntryExtendedModel[];
    onChangePageSize: (pageSize: number) => void;
    onClickUploadMapping?: () => void;
    onSetPage: (page: number) => void;
    pageSize: number;
    search?: string;
    selectedMappingEntries: CentralMappingMappingEntryExtendedModel[];
    setFilterOption: (filter: FilterOption) => void;
    setSelectedMappingEntries: (
      rows: CentralMappingMappingEntryExtendedModel[],
    ) => void;
    setValueType: (valueType: PmsValueType) => void;
    spiderMarketId?: string | null;
    startIndexPage: number;
    totalNoOfItems: number;
    valueType: PmsValueType;
  };
}

export default class HotelSettingsCentralMappingTablesTableMapping extends Component<HotelSettingsCentralMappingTablesTableMappingSignature> {
  @service declare access: Services['access'];
  @service declare centralMapping: Services['central-mapping'];
  @service declare intl: Services['intl'];
  @service declare toast: Services['toast'];
  @service declare usageAnalytics: Services['usage-analytics'];

  @tracked isMappingDropdownOpen = false;
  @tracked searchInputValue = '';

  openCostProfileSettings = (): void => {
    this.centralMapping.openCostSettings(this.args.spiderMarketId);
  };

  onSearchInputTask = task(
    { restartable: true },
    async (event: Event): Promise<void> => {
      this.searchInputValue = (event.target as HTMLInputElement)?.value;

      this.persistSelectedRows();

      await timeout('extra-long');

      this.usageAnalytics.track('Mapping Data Searched', {
        eventTrigger: 'mapping-search',
      });
    },
  );

  get columns(): CustomEmberTableColumn[] {
    const isMarketSegmentBreakdown =
      this.args.breakdown === BREAKDOWNS.MARKET_SEGMENT;
    const isRoomTypeBreakdown = this.args.breakdown === BREAKDOWNS.ROOM_TYPE;
    const cols: CustomEmberTableColumn[] = [];

    cols.push({
      name: this.intl.t('general.value'),
      textAlign: 'left' as const,
      tdComponent: SettingsCentralMappingTablesCellsTdValue,
      valuePath: COLUMN_PROPERTIES.VALUE,
      width: 100,
      isReadOnly: this.isDisabled,
    });

    cols.push({
      name: this.intl.t('general.description'),
      textAlign: 'left' as const,
      tdComponent: HotelSettingsCentralMappingTablesCellsTdDescription,
      valuePath: COLUMN_PROPERTIES.DESCRIPTION,
      width: 150,
      isReadOnly: this.isDisabled,
    });

    const showAutoMappingBadge =
      this.args.valueType === PMS_VALUE_TYPES.RATE_CODE &&
      !this.args.isLoading &&
      !this.args.isMappingReadOnly;

    cols.push({
      name: this.intl.t('general.mapping'),
      textAlign: 'left' as const,
      tdComponent: HotelSettingsCentralMappingTablesCellsTdMapping,
      thComponent: showAutoMappingBadge
        ? HotelSettingsCentralMappingTablesCellsThAutomationBadge
        : undefined,
      tdClassNames: 'table-column--with-possible-unmapped-background',
      valuePath: COLUMN_PROPERTIES.MAPPING,
      hierarchyCategories: this.args.hierarchyCategories,
      width: 300,
      isReadOnly: this.isDisabled,
    });

    if (this.hasCostProfileColumn) {
      cols.push({
        name: this.intl.t('settings.central_mapping.mapping.cost'),
        textAlign: 'left' as const,
        tdComponent: HotelSettingsCentralMappingTablesCellsTdCostProfile,
        thComponent:
          !this.args.isLoading && !this.args.isMappingReadOnly
            ? HotelSettingsCentralMappingTablesCellsThAutomationBadge
            : undefined,
        valuePath: COLUMN_PROPERTIES.COST_PROFILE,
        costProfiles: this.costProfiles,
        width: 200,
        isReadOnly: this.isDisabled,
        // Only offered to hotels that can manage the costs of their Spider market.
        onNavigateToCostSettings: this.args.isCostManagedByMarket
          ? this.openCostProfileSettings
          : undefined,
      });
    }

    if (isMarketSegmentBreakdown) {
      cols.push({
        name: this.intl.t('settings.central_mapping.mapping.bar_based'),
        textAlign: 'left' as const,
        tdComponent: HotelSettingsCentralMappingTablesCellsTdBarBased,
        thComponent: showAutoMappingBadge
          ? HotelSettingsCentralMappingTablesCellsThAutomationBadge
          : undefined,
        valuePath: COLUMN_PROPERTIES.BAR_BASED,
        width: 200,
        isReadOnly: this.isDisabled,
      });

      // Commissionable only applies to rate plans (the rate_code value type).
      if (this.args.valueType === PMS_VALUE_TYPES.RATE_CODE) {
        cols.push({
          name: this.intl.t('settings.central_mapping.mapping.commissionable'),
          textAlign: 'left' as const,
          tdComponent: HotelSettingsCentralMappingTablesCellsTdCommissionable,
          thComponent: showAutoMappingBadge
            ? HotelSettingsCentralMappingTablesCellsThAutomationBadge
            : undefined,
          valuePath: COLUMN_PROPERTIES.COMMISSIONABLE,
          width: 200,
          isReadOnly: this.isDisabled,
        });
      }
    }

    if (isRoomTypeBreakdown) {
      cols.push({
        name: this.intl.t('settings.central_mapping.mapping.inventory'),
        textAlign: 'left' as const,
        thComponent: HotelSettingsCentralMappingTablesCellsThInventory,
        tdComponent: HotelSettingsCentralMappingTablesCellsTdNumber,
        valuePath: COLUMN_PROPERTIES.INVENTORY,
        width: 150,
        isReadOnly: this.isDisabled,
        maxValue: MAX_ROOM_INVENTORY,
        valueKey: COLUMN_PROPERTIES.INVENTORY,
        placeholder: this.intl.t(
          'settings.central_mapping.mapping.number_of_rooms',
        ),
        tdNumberDataTestId: 'central-mapping-td-number-inventory',
      });

      cols.push({
        name: this.intl.t('settings.central_mapping.mapping.max_adults'),
        textAlign: 'left' as const,
        thComponent: HotelSettingsCentralMappingTablesCellsThMaxAdults,
        tdComponent: HotelSettingsCentralMappingTablesCellsTdNumber,
        valuePath: COLUMN_PROPERTIES.MAX_ADULTS,
        width: 150,
        isReadOnly: this.isDisabled,
        maxValue: MAX_ADULTS_IN_ROOM,
        valueKey: COLUMN_PROPERTIES.MAX_ADULTS,
        placeholder: this.intl.t(
          'settings.central_mapping.mapping.max_adults.placeholder',
        ),
        tdNumberDataTestId: 'central-mapping-td-number-max-adults',
      });
    }

    return cols;
  }

  get costProfiles(): CostSettingProfile[] {
    return this.args.costProfiles ?? [];
  }

  /*
   * Rate codes only appear once a booking has been processed, so on the rate group
   * breakdown an empty *search* result is likely a rate code that has not been booked
   * yet. Only surface the hint when the user is actively searching — either the on-page
   * search or the "Search all" filter. An empty result from a filter alone (e.g. a
   * category with no rate codes mapped) is legitimate and keeps showing the title only.
   */
  get emptyStateDescription(): string {
    const hasActiveSearch =
      this.searchInputValue.trim().length > 0 ||
      (this.args.search ?? '').trim().length > 0;

    if (this.isStayRestrictionRateGroupBreakdown && hasActiveSearch) {
      return this.intl.t(
        'settings.central_mapping.mapping.nothing_matches_filters_description',
      );
    }

    return '';
  }

  // Cached decorator gives a big performance boost when bulk mapping many table rows.
  @cached
  get filteredMapping(): CentralMappingMappingEntryExtendedModel[] {
    return (
      this.args.mapping
        // eslint-disable-next-line @typescript-eslint/unbound-method
        .filter(this.filterRowsByDropdownOption)
        // eslint-disable-next-line @typescript-eslint/unbound-method
        .filter(this.filterRowsBySearch)
    );
  }

  get formattedEndIndexPage(): string {
    return this.intl.formatNumber(this.args.endIndexPage);
  }

  get formattedStartIndexPage(): string {
    return this.intl.formatNumber(this.args.startIndexPage);
  }

  get formattedTotalNoOfItems(): string {
    return this.intl.formatNumber(this.args.totalNoOfItems);
  }

  // Cost profiles belong to the Spider market, so a hotel outside one has no cost profile column.
  get hasCostProfileColumn(): boolean {
    return (
      this.args.breakdown === BREAKDOWNS.DISTRIBUTION_CHANNEL &&
      !!this.args.spiderMarketId
    );
  }

  get isDisabled(): boolean {
    const { isMappingReadOnly, isSaving } = this.args;

    if (this.isStayRestrictionRateGroupBreakdown) {
      return (
        !this.access.hasPricingAssistantRestrictionsSettingsEditRights ||
        isSaving
      );
    }

    return isMappingReadOnly || isSaving;
  }

  get isStayRestrictionRateGroupBreakdown(): boolean {
    return this.args.breakdown === BREAKDOWNS.STAY_RESTRICTION_RATE_GROUP;
  }

  /**
   * Add the `cached` decorator to this getter, otherwise we can't unselect rows in the table.
   * The decorator preserves references for equality checks in ember-table.
   */
  @cached
  get rows(): TableMappingRow[] {
    const result = this.filteredMapping.map((mappingEntry) => {
      const { breakdown } = this.args;
      // For sorting: to sort on the mapping column, we join the category path into 1 string that we can sort on.
      // E.g. ['Transient', 'my custom category', 'my custom subcategory'] becomes 'Transient,my custom category,my custom subcategory'.
      const categoryPath = mappingEntry.category?.get('path') ?? [];
      const mappingSortString = categoryPath.map((cat) => cat.name).join();

      const row: TableMappingRow = {
        className: mappingEntry.isUnmapped ? 'table-row--unmapped' : '',
        [COLUMN_PROPERTIES.VALUE]: mappingEntry[COLUMN_PROPERTIES.VALUE],
        [COLUMN_PROPERTIES.DESCRIPTION]:
          mappingEntry[COLUMN_PROPERTIES.DESCRIPTION],
        [COLUMN_PROPERTIES.MAPPING]: mappingSortString,
        toggleAutomapping: (category: CentralMappingCategory | null) => {
          this.setCategory([mappingEntry], category);
          const mappingChange = category
            ? 'automapping-disabled'
            : 'automapping-enabled';

          if (category) {
            this.usageAnalytics.track('Mapping Automation Disabled', {
              eventTrigger: 'disable-automapping',
              mappingChange: 'mapping',
            });
          }
          this.centralMapping.trackMappingEntryChanged({ mappingChange });
        },
        onOverrideAutomapping: () => {
          this.usageAnalytics.track('Mapping Automation Overwritten', {
            eventTrigger: 'override-automapping',
            mappingChange: 'mapping',
          });
        },
        setCategory: (category: CentralMappingCategory | null) => {
          this.setCategory([mappingEntry], category);

          if (mappingEntry.category) {
            this.centralMapping.trackMappingEntryChanged({
              mappingChange: 'mapping',
            });
          }
        },
        setBarBased: (value: boolean | null) => {
          this.setBarBased([mappingEntry], value);

          this.centralMapping.trackMappingEntryChanged({
            mappingChange: 'barBased',
          });
        },
        setCommissionable: (value: Commissionable) => {
          this.setCommissionable([mappingEntry], value);

          this.centralMapping.trackMappingEntryChanged({
            mappingChange: 'commissionable',
          });
        },
        setCostProfile: (costProfile: CostSettingProfile | null) => {
          this.setCostProfile([mappingEntry], costProfile);

          this.centralMapping.trackMappingEntryChanged({
            mappingChange: COLUMN_PROPERTIES.COST_PROFILE,
          });
        },
        mappingEntry,
      };

      // @ts-expect-error: Incorrect type
      if (mappingEntryHasBarBased(mappingEntry)) {
        row[COLUMN_PROPERTIES.BAR_BASED] =
          mappingEntry[COLUMN_PROPERTIES.BAR_BASED];
      }

      if (mappingEntryHasCommissionable(mappingEntry)) {
        row[COLUMN_PROPERTIES.COMMISSIONABLE] =
          mappingEntry[COLUMN_PROPERTIES.COMMISSIONABLE];
      }

      if (mappingEntryHasCostProfile(mappingEntry)) {
        row[COLUMN_PROPERTIES.COST_PROFILE] =
          mappingEntry[COLUMN_PROPERTIES.COST_PROFILE];
      }

      if (
        breakdown === BREAKDOWNS.ROOM_TYPE &&
        // @ts-expect-error: Incorrect type
        isMappingEntryRoomType(mappingEntry)
      ) {
        row[COLUMN_PROPERTIES.INVENTORY] =
          mappingEntry[COLUMN_PROPERTIES.INVENTORY];
        row[COLUMN_PROPERTIES.MAX_ADULTS] =
          mappingEntry[COLUMN_PROPERTIES.MAX_ADULTS];
      }

      return row;
    });

    return result;
  }

  get searchIcon(): {
    name: SearchInputIconType;
    onClick: (() => void) | undefined;
  } {
    const isEditing = this.searchInputValue.length > 0;
    let name: SearchInputIconType = SEARCH_INPUT_ICONS.SEARCH;
    if (isEditing) {
      name = SEARCH_INPUT_ICONS.CROSS;
    }

    const onClick = isEditing
      ? (): void => this.manageSearchIconClick(name)
      : undefined;

    return { name, onClick };
  }

  get selectedRows(): TableMappingRow[] {
    return this.rows.filter((row) =>
      this.args.selectedMappingEntries.includes(row.mappingEntry),
    );
  }

  get showCommissionableFilter(): boolean {
    return (
      this.args.breakdown === BREAKDOWNS.MARKET_SEGMENT &&
      this.args.valueType === PMS_VALUE_TYPES.RATE_CODE
    );
  }

  get showExportButton(): boolean {
    return (
      this.args.breakdown === BREAKDOWNS.MARKET_SEGMENT &&
      this.args.valueType === PMS_VALUE_TYPES.RATE_CODE
    );
  }

  get showExportSection(): boolean {
    return !this.isStayRestrictionRateGroupBreakdown;
  }

  get showingText(): string {
    return this.intl.t('settings.central-mapping.mapping.page_showing', {
      currentPage: this.formattedStartIndexPage,
      lastPage: this.formattedEndIndexPage,
      totalRecords: this.formattedTotalNoOfItems,
    });
  }

  get showPaginationText(): number {
    const { totalNoOfItems, startIndexPage, endIndexPage } = this.args;
    return (
      totalNoOfItems &&
      startIndexPage &&
      endIndexPage &&
      this.filteredMapping.length
    );
  }

  get uploadDropdownOptions(): { icon: 'upload'; id: string; label: string }[] {
    return [
      {
        id: 'upload-mapping',
        label: this.intl.t('settings.central_mapping.upload_mapping'),
        icon: 'upload',
      },
    ];
  }

  get visibleTabIds(): PmsValueType[] {
    const breakdownRecord = this.centralMapping.breakdowns.find(
      (b) => b.breakdown === this.args.breakdown,
    );

    if (!breakdownRecord) {
      return [];
    }

    return breakdownRecord.valueTypes
      .slice()
      .filter((valueTypeRecord) => valueTypeRecord.available)
      .map((valueTypeRecord) => valueTypeRecord.valueType)
      .sort(
        (a, b) =>
          Object.values(PMS_VALUE_TYPES).indexOf(a) -
          Object.values(PMS_VALUE_TYPES).indexOf(b),
      );
  }

  manageSearchIconClick(name: SearchInputIconType): void {
    if (name === SEARCH_INPUT_ICONS.CROSS) {
      this.searchInputValue = '';
    }
  }

  @action
  applyMappingFilters(appliedFilters: AppliedMappingFilterIds): void {
    this.isMappingDropdownOpen = false;
    this.args.applyFilters(appliedFilters);
  }

  @action
  filterRowsByDropdownOption(
    row: CentralMappingMappingEntryExtendedModel,
  ): boolean {
    const { filterOption } = this.args;

    switch (filterOption) {
      case FILTER_OPTIONS.UNMAPPED:
        return row.isUnmapped;
      case FILTER_OPTIONS.AUTOMATED:
        // Need to narrow the type to make sure TS doesn't complain about the `isAutomapped` property.
        return row.isAutomapped;
      case FILTER_OPTIONS.UNSAVED:
        return row.isUnsaved;
      case FILTER_OPTIONS.ALL:
      default:
        return true;
    }
  }

  @action
  filterRowsBySearch(
    mappingEntry: CentralMappingMappingEntryExtendedModel,
  ): boolean {
    // Convert strings to lowercase to make the filtering case-insensitive
    const searchInputValueLowercase = this.searchInputValue
      .toLowerCase()
      .trim();

    const valueLowercase = mappingEntry[COLUMN_PROPERTIES.VALUE]
      .toLowerCase()
      .trim();
    const descriptionLowercase = mappingEntry[COLUMN_PROPERTIES.DESCRIPTION]
      .toLowerCase()
      .trim();

    const valueIncludesSearchTerm = valueLowercase.includes(
      searchInputValueLowercase,
    );
    const descriptionIncludesSearchTerm = descriptionLowercase.includes(
      searchInputValueLowercase,
    );

    return valueIncludesSearchTerm || descriptionIncludesSearchTerm;
  }

  @action
  getTabTranslationKey(tabId: string, breakdown: Breakdown): string {
    if (breakdown === BREAKDOWNS.STAY_RESTRICTION_RATE_GROUP) {
      return tKey('settings.central_mapping.stay_restriction_rate_group');
    }
    if (
      tabId === PMS_VALUE_TYPES.RATE_CODE &&
      breakdown === BREAKDOWNS.RATE_CODE
    ) {
      return tKey('settings.central_mapping.package');
    }
    return PMS_VALUE_TYPE_TRANSLATIONS[tabId as PmsValueType] ?? '';
  }

  @action
  handleMappingExport(): void {
    const { valueType, breakdown, appliedFilters } = this.args;

    this.centralMapping.exportMappingData(
      breakdown,
      valueType,
      appliedFilters ?? {},
    );

    this.usageAnalytics.track('Mapping Data Exported', {
      eventTrigger: 'ratecode-export',
    });
  }

  @action
  handleUploadMapping(): void {
    this.args.onClickUploadMapping?.();

    this.usageAnalytics.track('Mapping Upload Started', {
      eventTrigger: 'upload-button-clicked',
    });
  }

  @action
  onChangePageSize(pageSizeOption: { value: number }): void {
    this.args.onChangePageSize(pageSizeOption.value);
  }

  @action
  onRowSelect(rows: CustomEmberTableRow | CustomEmberTableRow[]): void {
    const typedRows = (
      Array.isArray(rows) ? rows : [rows]
    ) as TableMappingRow[];

    this.args.setSelectedMappingEntries(
      typedRows.map((row) => row.mappingEntry),
    );
  }

  @action
  onSelectTab(tabId: string): void {
    if (!isPmsValueType(tabId)) {
      return;
    }

    this.args.setValueType(tabId);
    this.persistSelectedRows();
  }

  @action
  persistSelectedRows(): void {
    this.args.setSelectedMappingEntries(
      this.selectedRows.map((row) => row.mappingEntry),
    );
  }

  @action setBarBased(
    mappingEntries: CentralMappingMappingEntryExtendedModel[],
    value: BarBased,
  ): void {
    mappingEntries
      .filter(
        // @ts-expect-error: Incorrect type
        (mappingEntry) => mappingEntry && mappingEntryHasBarBased(mappingEntry),
      )
      .forEach((mappingEntry) => {
        if (!mappingEntry) {
          return;
        }

        // If user deselets and then re-selects the auto-mapped value, it shouldn't count as a change.
        // We prevent userBarBased being set and hasDirtyAttributes becoming true.
        if (
          value === mappingEntry.autoBarBased &&
          isEmpty(mappingEntry.userBarBased)
        ) {
          mappingEntry.userBarBased = null;
        } else {
          mappingEntry.userBarBased = value;
        }
      });

    this.showToastSelectionsChanged(mappingEntries);
    this.showToastRowsHidden(mappingEntries);
  }

  @action setCategory(
    mappingEntries: CentralMappingMappingEntryExtendedModel[],
    category: CentralMappingCategory | null,
  ): void {
    mappingEntries.forEach((mappingEntry) => {
      mappingEntry.userCategory = category;
    });

    this.showToastSelectionsChanged(mappingEntries);
    this.showToastRowsHidden(mappingEntries);
  }

  @action setCommissionable(
    mappingEntries: CentralMappingMappingEntryExtendedModel[],
    value: Commissionable,
  ): void {
    mappingEntries
      .filter(
        (mappingEntry) =>
          mappingEntry && mappingEntryHasCommissionable(mappingEntry),
      )
      .forEach((mappingEntry) => {
        if (!mappingEntry) {
          return;
        }

        // If user deselects and then re-selects the auto-mapped value, it shouldn't count as a change.
        // We prevent userCommissionable being set and hasDirtyAttributes becoming true.
        if (
          value === mappingEntry.autoCommissionable &&
          isEmpty(mappingEntry.userCommissionable)
        ) {
          mappingEntry.userCommissionable = null;
        } else {
          mappingEntry.userCommissionable = value;
        }
      });

    this.showToastSelectionsChanged(mappingEntries);
    this.showToastRowsHidden(mappingEntries);
  }

  @action setCostProfile(
    mappingEntries: CentralMappingMappingEntryExtendedModel[],
    costProfile: CostSettingProfile | null,
  ): void {
    const mappingEntriesWithCostProfile = mappingEntries.filter(
      mappingEntryHasCostProfile,
    );

    const hasChanges = mappingEntriesWithCostProfile.some((mappingEntry) => {
      return (
        mappingEntry.userCostProfile !==
        getUserCostProfileToStore(mappingEntry, costProfile)
      );
    });

    mappingEntriesWithCostProfile.forEach((mappingEntry) => {
      mappingEntry.userCostProfile = getUserCostProfileToStore(
        mappingEntry,
        costProfile,
      );
    });

    if (hasChanges) {
      this.showToastSelectionsChanged(mappingEntries);
      this.showToastRowsHidden(mappingEntries);
    }
  }

  @action
  setFilterOption(filter: FilterOption): void {
    this.args.setFilterOption(filter);

    this.persistSelectedRows();

    if (filter === FILTER_OPTIONS.UNSAVED) {
      this.usageAnalytics.track('Unsaved Changes Viewed', {
        eventTrigger: 'view-unsaved',
      });
    }
  }

  @action
  setIsMappingDropdownOpen(isOpen: boolean): void {
    this.isMappingDropdownOpen = isOpen;
  }

  @action
  showToastRowsHidden(
    mappingEntries: CentralMappingMappingEntryExtendedModel[],
  ): void {
    const didAnyRowBecomeHidden = mappingEntries.some((mappingEntry) => {
      return !this.filteredMapping.includes(mappingEntry);
    });

    // Check if mapping any of the rows removed it from the table rows. If so, show a toast to make the user aware of it.
    if (didAnyRowBecomeHidden) {
      this.persistSelectedRows();

      this.toast.success(
        this.intl.t('settings.central_mapping.mapping.toast.hid_row'),
        {
          showIcon: true,
        },
      );
    }
  }

  @action showToastSelectionsChanged(
    mappingEntries: CentralMappingMappingEntryExtendedModel[],
  ): void {
    const isMultipleRows = mappingEntries.length > 1;

    if (isMultipleRows) {
      this.toast.success(
        this.intl.t(
          'settings.central_mapping.mapping.count_selections_changed',
          {
            count: mappingEntries.length,
          },
        ),
        {
          showIcon: true,
        },
      );
    }
  }

  <template>
    <PrismTabsContainer
      @activeTabId={{@valueType}}
      @onSelectTab={{this.onSelectTab}}
      @visibleTabIds={{this.visibleTabIds}}
      as |TabsContainer|
    >
      <PrismCard @contentLayout="noPadding" class="h-full">
        <:content>
          <div
            class="central-mapping__table-mapping__header flex flex-row items-center justify-between"
          >
            {{#if (gt this.visibleTabIds.length 1)}}
              <TabsContainer.header>
                <:default as |Header|>
                  {{#each this.visibleTabIds as |tabId|}}
                    <Header.tab
                      @id={{tabId}}
                      data-test-id="central-mapping-table-mapping-header-tab"
                    >
                      {{t (this.getTabTranslationKey tabId @breakdown)}}
                    </Header.tab>
                  {{/each}}
                </:default>
              </TabsContainer.header>
            {{else}}
              <div
                class="text-400--bold px-400 py-300"
                data-test-id="central-mapping-table-mapping-header"
              >{{t
                  (this.getTabTranslationKey
                    (get this.visibleTabIds 0) @breakdown
                  )
                }}</div>
            {{/if}}

            <div class="flex flex-row gap-200 px-400 py-300">
              {{#unless @isMappingReadOnly}}
                <SettingsCentralMappingDropdownsDropdownFilter
                  @breakdown={{@breakdown}}
                  @filterOption={{@filterOption}}
                  @mapping={{@mapping}}
                  @setFilterOption={{this.setFilterOption}}
                />
              {{/unless}}

              <PrismInput
                @iconAfter={{this.searchIcon}}
                @name="search"
                @onInput={{this.onSearchInputTask.perform}}
                @placeholder={{t
                  "settings.central_mapping.search_on_page.placeholder"
                }}
                @removePadding={{true}}
                @type="text"
                @value={{this.searchInputValue}}
              />

              {{! @glint-expect-error: Incorrect type }}
              <SettingsCentralMappingDropdownsDropdownMappingFilter
                @appliedFilters={{@appliedFilters}}
                @applyFilters={{this.applyMappingFilters}}
                @breakdown={{@breakdown}}
                @hideSubscriptionFilters={{true}}
                @hierarchyCategories={{@hierarchyCategories}}
                @isOpen={{this.isMappingDropdownOpen}}
                @onToggleDropdown={{this.setIsMappingDropdownOpen}}
                @showCommissionableFilter={{this.showCommissionableFilter}}
              />

              {{#if this.showExportSection}}
                <div>
                  {{#if @hasEditRights}}
                    <div class={{styles.split-button}}>
                      <PrismButton
                        @buttonType="secondary"
                        @iconBefore="export"
                        @isDisabled={{@hasUnsavedChanges}}
                        @label={{t "general.export"}}
                        @onClick={{this.handleMappingExport}}
                        data-test-id="central-mapping-export-button"
                      />
                      <PrismComboSelectInput
                        @disabled={{@hasUnsavedChanges}}
                        @horizontalPosition="right"
                        @onChange={{this.handleUploadMapping}}
                        @options={{this.uploadDropdownOptions}}
                        @unsafe_triggerComponent={{component
                          GeneralDropdownsTriggersTriggerPowerSelectChevron
                          isDisabled=@hasUnsavedChanges
                        }}
                        data-test-id="central-mapping-upload-dropdown"
                      />
                    </div>
                  {{else}}
                    <PrismButton
                      @buttonType="secondary"
                      @iconBefore="export"
                      @isDisabled={{@hasUnsavedChanges}}
                      @label={{t "general.export"}}
                      @onClick={{this.handleMappingExport}}
                      data-test-id="central-mapping-export-button"
                    />
                  {{/if}}

                  {{#if @hasUnsavedChanges}}
                    <PrismTooltip
                      @side="top"
                      @text={{t
                        "settings.central-mapping.mapping.export.tooltip"
                      }}
                    />
                  {{/if}}
                </div>
              {{/if}}
            </div>
          </div>

          <div class="central-mapping__table-mapping">
            <PrismTable
              @checkboxSelectionMode={{unless
                this.isDisabled
                "multiple"
                undefined
              }}
              @columns={{this.columns}}
              @fillMode="last-column"
              @isLoading={{@isLoading}}
              @onRowSelect={{unless this.isDisabled this.onRowSelect undefined}}
              @oneCellBulkEditRow={{false}}
              @removeTableBorder={{true}}
              @renderAll={{false}}
              @rowSelection={{unless
                this.isDisabled
                this.selectedRows
                undefined
              }}
              @rows={{this.rows}}
              class="central-mapping__table-mapping__table"
              data-test-id="table-central-mapping"
            >
              <:bulkEditRow as |bulkEditRow|>
                <HotelSettingsCentralMappingTablesTableMappingBulkEditRow
                  @barBased={{hash
                    setBarBased=(fn this.setBarBased @selectedMappingEntries)
                  }}
                  @breakdown={{@breakdown}}
                  @bulkEditRow={{bulkEditRow}}
                  @commissionable={{hash
                    setCommissionable=(fn
                      this.setCommissionable @selectedMappingEntries
                    )
                  }}
                  @costProfile={{hash
                    isAvailable=this.hasCostProfileColumn
                    onNavigateToCostSettings=(if
                      @isCostManagedByMarket this.openCostProfileSettings
                    )
                    profiles=this.costProfiles
                    setCostProfile=(fn
                      this.setCostProfile @selectedMappingEntries
                    )
                  }}
                  @mapping={{hash
                    hierarchyCategories=@hierarchyCategories
                    setCategory=(fn this.setCategory @selectedMappingEntries)
                  }}
                  @selectedMappingEntries={{@selectedMappingEntries}}
                  @valueType={{@valueType}}
                />
              </:bulkEditRow>

              <:emptyState>
                <div class="flex h-full items-center justify-center">
                  <PrismMessage
                    @description={{this.emptyStateDescription}}
                    @illustration="100x100_search"
                    @title={{t
                      "settings.central_mapping.mapping.nothing_matches_filters"
                    }}
                  />
                </div>
              </:emptyState>
            </PrismTable>
          </div>
        </:content>

        <:footer>
          {{#if this.showPaginationText}}
            <GeneralTablesPaginationFooter
              @currentPage={{@currentPage}}
              @lastPage={{@lastPage}}
              @onChangePageSize={{this.onChangePageSize}}
              @onSetPage={{@onSetPage}}
              @pageSizeDropdown={{GeneralDropdownsDropdownPageSize}}
              @pageSizeValue={{@curentPageSizeValueOption}}
              @showingText={{this.showingText}}
              @testIdPrefix="central-mapping-table-mapping"
            />
          {{/if}}
        </:footer>
      </PrismCard>
    </PrismTabsContainer>
  </template>
}
